import type { RaiserApplication } from "@workspace/api-client-react";
import { format } from "date-fns";

type FieldDef = {
  key: keyof RaiserApplication;
  label: string;
  map?: Record<string, string>;
};

type Section = { title: string; fields: FieldDef[] };

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatValue(value: unknown, map?: Record<string, string>): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((v) => map?.[String(v)] ?? String(v)).join(", ");
  }
  return map?.[String(value)] ?? String(value);
}

const STATUS_LABELS: Record<string, string> = {
  new: "New",
  under_review: "Under Review",
  accepted: "Accepted",
  declined: "Declined",
};

export function buildPrintHtml(
  app: RaiserApplication,
  sections: Section[],
): string {
  const submitted = format(new Date(app.createdAt), "MMMM d, yyyy");
  const status = STATUS_LABELS[app.status] ?? app.status;

  const sectionsHtml = sections
    .map((section) => {
      const rows = section.fields
        .map((f) => {
          const value = escapeHtml(formatValue(app[f.key], f.map));
          return `<tr><td class="label">${escapeHtml(f.label)}</td><td class="value">${value}</td></tr>`;
        })
        .join("");
      return `<section><h2>${escapeHtml(section.title)}</h2><table>${rows}</table></section>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Puppy Raiser Application — ${escapeHtml(app.fullName)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: Georgia, "Times New Roman", serif;
    color: #1a1a1a;
    margin: 0;
    padding: 32px 40px;
    font-size: 12px;
    line-height: 1.45;
  }
  header {
    border-bottom: 2px solid #1a1a1a;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }
  header h1 {
    font-size: 20px;
    margin: 0 0 2px;
  }
  header .org {
    font-size: 13px;
    font-weight: bold;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin: 0 0 8px;
    color: #444;
  }
  header .meta {
    font-size: 11px;
    color: #444;
  }
  header .meta span + span::before {
    content: "  •  ";
    white-space: pre;
  }
  section { break-inside: avoid-page; margin-bottom: 18px; }
  h2 {
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #999;
    padding-bottom: 3px;
    margin: 0 0 8px;
  }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 3px 0; }
  td.label { width: 34%; color: #555; padding-right: 16px; }
  td.value { white-space: pre-wrap; }
  footer {
    margin-top: 24px;
    padding-top: 8px;
    border-top: 1px solid #999;
    font-size: 10px;
    color: #666;
  }
  @media print {
    body { padding: 0; }
  }
  @page { margin: 18mm 16mm; }
</style>
</head>
<body>
<header>
  <p class="org">Greater Hope Service Dogs</p>
  <h1>Puppy Raiser Application — ${escapeHtml(app.fullName)}</h1>
  <p class="meta">
    <span>Submitted ${escapeHtml(submitted)}</span>
    <span>Status: ${escapeHtml(status)}</span>
    <span>${escapeHtml(app.email)}</span>
  </p>
</header>
${sectionsHtml}
<footer>Application #${app.id} — printed ${escapeHtml(format(new Date(), "MMMM d, yyyy"))}. Confidential: for board and staff review only.</footer>
</body>
</html>`;
}

export function printApplication(
  app: RaiserApplication,
  sections: Section[],
): boolean {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) return false;
  win.document.open();
  win.document.write(buildPrintHtml(app, sections));
  win.document.close();
  win.focus();
  const doPrint = () => {
    win.print();
  };
  if (win.document.readyState === "complete") {
    setTimeout(doPrint, 150);
  } else {
    win.addEventListener("load", () => setTimeout(doPrint, 150));
  }
  return true;
}
